import { LANG_EN, LANG } from "@/constants/Language/Language";
import { GRADEANDLEXILELEVELS } from "@/constants/GradeAndLexileLevels";
import {
  IRecommendedReadingLevel,
  ILegacyStudentReadingLevel,
} from "@/types/models/ReadingLevel.interface";
import { User } from "@/types/models/User.interface";
import { findHighestLevelBelowReadingLevel } from "./findHighestLevelBelowReadingLevel";
import { findHighestLexileWithinRange } from "./findHighestLexileWithinRange";
import { getOriginalLevel } from "./getOriginalLevel";

export interface ContentLevel {
  id: string;
  lexile: string;
  raw_lexile: number;
  grade_band: string;
  language: LANG;
  is_original_level: boolean;
}

// TODO: Refactor needed. Downstream functions expect contentLevels to be a sorted array based on the levels raw_lexile value.
// This should be reflected in variable naming. We should also remove the sorting within this function.
export const findDefaultLevelFromReadingOrGradeLevel = (
  user: User,
  contentLevels: ContentLevel[] = [],
  language: LANG,
  studentReadingLevel:
    | ILegacyStudentReadingLevel
    | IRecommendedReadingLevel
    | undefined,
) => {
  const gradeLevel = user?.grade_level;
  const lowestClassroomGradeLevel = user?.classroom_grade_levels?.[0];
  const readingLevel =
    studentReadingLevel && "value" in studentReadingLevel
      ? studentReadingLevel?.value
      : studentReadingLevel?.recommended_grade_level;
  // If a Spanish level should be displayed, a level query param should be added.
  // The level setting will then be handled in getCurrentLevel.
  const englishLevels = contentLevels.filter(
    (level) => level.language === LANG_EN,
  );
  const levelsBasedOnLanguage = !language
    ? englishLevels
    : contentLevels.filter((level) => level.language === language);
  const sortedContentLevels = [...levelsBasedOnLanguage].sort(
    (a, b) => a.raw_lexile - b.raw_lexile,
  );
  const flooredReadingLevel =
    readingLevel !== null && typeof readingLevel === "number"
      ? Math.floor(readingLevel)
      : readingLevel;
  const gradeAndLexileIndex =
    flooredReadingLevel || lowestClassroomGradeLevel || gradeLevel;
  const readingOrGradeLevelLexileRange =
    GRADEANDLEXILELEVELS[gradeAndLexileIndex]?.rawLexileRange || [];

  // If no range is provided for reading level (grade band 11 and 12 range is "use original level").
  if (!readingOrGradeLevelLexileRange.length) {
    return getOriginalLevel(contentLevels, language);
  }

  const highestLevelInLexileRange = findHighestLexileWithinRange(
    sortedContentLevels,
    readingOrGradeLevelLexileRange,
    language,
  );

  if (highestLevelInLexileRange) {
    return highestLevelInLexileRange;
  }

  const highestLevelBelowReadingLevel = findHighestLevelBelowReadingLevel(
    sortedContentLevels,
    readingOrGradeLevelLexileRange,
    language,
  );

  if (highestLevelBelowReadingLevel) {
    return highestLevelBelowReadingLevel;
  }

  // If no level exists below student reading level, choose the lowest level.
  return sortedContentLevels[0];
};

export default findDefaultLevelFromReadingOrGradeLevel;
